import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InfoIcon from '@mui/icons-material/Info';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { getSubProrateCharges, manageSelectedUsers } from 'wisestep-api';
import {
  ControlledTextInput,
  Item,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useWorkspaceSKUContext } from '../../../context';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { ICouponData } from '../types/ICoupon';
import { IPaymentInfo } from '../types/IPaymentInfo';
import { getPlanCode, getPlanRateByInterval } from '../utils/helper';

import CouponLisiting from './CouponLisiting';
import PaymentContext from './PaymentContext';

interface IUserSubInfo {
  couponDiscount: string | null;
  coupon: string | null;
  proratedAmount: string;
  mode: string;
}

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: WsColors.TertiaryShades100,
    color: WsColors.GenericRichBlack,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const AlingTextSideBySide = ({
  leftText,
  rightText,
  leftTextCaption,
  primary,
}: {
  leftText: string;
  rightText: string;
  leftTextCaption?: string;
  primary?: boolean;
}) => (
  <Grid container mt={2}>
    <Grid item xs={6} textAlign="left">
      <WsTypography bold> {leftText}</WsTypography>
      {leftTextCaption && (
        <WsTypography bold variant={WsTypoVariants.BODY2} color={WsTypoColors.QUINARY}>
          {leftTextCaption}
        </WsTypography>
      )}
    </Grid>
    <Grid item xs={6} textAlign="right">
      <WsTypography bold color={primary ? WsTypoColors.PRIMARY : WsTypoColors.BLACK}>
        {rightText}
      </WsTypography>
    </Grid>
    <Divider sx={{ width: '100%', mt: 1 }} />
  </Grid>
);

const ManagePlan = ({
  plan,
  user,
  handleClose,
}: {
  plan: IPaymentInfo;
  user: number;
  handleClose: () => void;
}) => {
  const [warnBeforePayment, setWarnBeforePayment] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const workspaceSKU = useWorkspaceSKUContext();
  const [tempUsers, setTempUsers] = useState<number>(0);
  const [subInfo, setSubInfo] = useState<IUserSubInfo | null>(null);
  const [totalCharge, setTotalCharge] = useState<string>('0');
  const [mode, setMode] = useState<string>('SUBSCRIPTION');
  const [coupon, setCoupon] = useState<ICouponData | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [proratedAmount, setProratedAmount] = useState<string>('0');
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm({
    defaultValues: {
      users: 0,
    },
  });
  const { setValue, watch, getValues } = methods;
  const totalUsers = watch('users');

  const getIntervalFromSKU = (
    startDate: string | null | undefined,
    endDate: string | null | undefined
  ) => {
    let interval = 'none';
    if (startDate && endDate) {
      const diffInMonth = dayjs(endDate).diff(dayjs(startDate), 'month');
      if (diffInMonth === 1) {
        interval = 'month';
      } else if (diffInMonth === 3) {
        interval = 'quater';
      } else if (diffInMonth === 12) {
        interval = 'annual';
      }
    }

    return interval;
  };

  const currentPlanInterval = getIntervalFromSKU(workspaceSKU.activeFrom, workspaceSKU.expiryDate);

  const currentRate = getPlanRateByInterval(plan.plan, currentPlanInterval);

  useEffect(() => {
    let perUserCharge = totalUsers * currentRate;
    switch (currentPlanInterval) {
      case 'month':
        perUserCharge = perUserCharge * 1;
        break;
      case 'quater':
        perUserCharge = perUserCharge * 3;
        break;
      case 'annual':
        perUserCharge = perUserCharge * 12;
        break;
    }
    setTotalCharge(perUserCharge.toString());
  }, [currentPlanInterval, currentRate, subInfo, totalUsers]);

  const onInputChange = async (e) => {
    const { value } = e.target;
    if (totalUsers != tempUsers && currentPlanInterval != 'none') {
      setProratedAmount('calculating');
      setTempUsers(totalUsers);
      if (value <= 0) {
        setNotification(failed(`Users cannot be less then 0`));
        setValue('users', 0);
        setTotalCharge('0');
      } else {
        const res = await getSubProrateCharges({
          amount: parseInt(totalCharge),
          productId: plan.productId,
          interval: currentPlanInterval,
          planStartDate: workspaceSKU?.activeFrom
            ? parseInt((new Date(workspaceSKU.activeFrom).getTime() / 1000).toFixed(0)).toString()
            : '',
        });
        setMode(res.data.mode);
        setProratedAmount((parseInt(res.data.proratedAmount) / 100).toString());
        setSubInfo(res.data);
      }
    }
  };

  const onSubmit = async () => {
    setDisableSubmit(true);
    const planInfo = getPlanCode(plan?.plan);
    if (!isEmpty(planInfo) && parseInt(totalCharge) > 0) {
      const res = await manageSelectedUsers({
        amount: mode === 'SUBSCRIPTION' ? parseInt(totalCharge) : parseFloat(proratedAmount),
        productId: plan.productId,
        interval: getIntervalFromSKU(workspaceSKU.activeFrom, workspaceSKU.expiryDate),
        customerId: plan.customerId,
        users: getValues('users')?.toString() ?? user.toString(),
        manual: true,
        planCode: planInfo,
        userManagement: true,
        perUserCharge: `$ ${currentRate}`,
      });
      setDisableSubmit(false);
      if (res.data.mode === 'auto') {
        window.location.href = window.location.href + '?success=true&update=true';
      } else {
        setClientSecret(res.data.clientSecret);
      }
    } else {
      setDisableSubmit(false);
      setNotification(
        failed(
          parseInt(totalCharge) <= 0 ? `You already have ${user} users` : 'Unable to detect plan'
        )
      );
    }
  };

  const onCouponSubmit = (couponData: ICouponData) => {
    if (couponData.coupon.percent_off) {
      if (coupon?.code != couponData.code) {
        setCoupon(couponData);
        if (!isEmpty(coupon)) {
          const charge = parseInt(proratedAmount);
          const discount: number = (charge * couponData.coupon.percent_off) / 100;
          setProratedAmount((charge - parseFloat(discount.toFixed(2))).toString());
        } else {
          const charge = subInfo?.proratedAmount ? parseInt(subInfo?.proratedAmount) / 100 : 0;
          const discount: number = (charge * couponData.coupon.percent_off) / 100;
          setProratedAmount((charge - parseFloat(discount.toFixed(2))).toString());
        }
      } else {
        setNotification(failed('This coupon is already applied'));
      }
    } else {
      setNotification(failed('No Discount Available'));
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setProratedAmount(
      subInfo?.proratedAmount ? (parseInt(subInfo?.proratedAmount) / 100).toString() : '0'
    );
  };

  const handleCoupon = () => {
    if (coupon?.coupon.name) {
      removeCoupon();
    }
  };

  const setPlanInterval = () => {
    let interval = '';
    switch (currentPlanInterval) {
      case 'month':
        interval = '1 Month';
        break;
      case 'quater':
        interval = '1 Quater';
        break;
      case 'annual':
        interval = '1 Year';
        break;
    }
    return interval;
  };

  const setPlanIntervalInMonths = () => {
    let interval = '';
    switch (currentPlanInterval) {
      case 'month':
        interval = '1 Month';
        break;
      case 'quater':
        interval = '3 Months';
        break;
      case 'annual':
        interval = '12 Months';
        break;
    }
    return interval;
  };

  return (
    <>
      {clientSecret ? (
        <PaymentContext secretKey={clientSecret} updateUser={true} />
      ) : (
        <Grid container>
          {!warnBeforePayment ? (
            <FormProvider {...methods}>
              <Grid item xs={12} textAlign="center">
                <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.PRIMARY} bold>
                  Your Cart
                </WsTypography>
              </Grid>
              <Grid item xs={12} mt={3}>
                <TableContainer>
                  <Table sx={{ minWidth: 700 }} aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>
                          <WsTypography bold>S.no</WsTypography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <WsTypography bold>Plan</WsTypography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <WsTypography bold>Price Per User</WsTypography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <WsTypography bold>Number of Users</WsTypography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <WsTypography bold>Plan interval</WsTypography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <WsTypography bold>Total</WsTypography>
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          <WsTypography bold color={WsTypoColors.QUINARY}>
                            1.
                          </WsTypography>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          <WsTypography bold color={WsTypoColors.QUINARY}>
                            {plan.plan}
                          </WsTypography>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          <WsTypography bold color={WsTypoColors.QUINARY}>
                            {`$${currentRate}`}
                          </WsTypography>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          <ControlledTextInput
                            name="users"
                            labelText=""
                            type="number"
                            fullWidth
                            id="users"
                            placeholder=""
                            InputProps={{ inputProps: { min: 1 } }}
                            variant="outlined"
                            autoComplete="users"
                            sx={{ width: 150 }}
                            onMouseLeave={onInputChange}
                          />
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {setPlanInterval()}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {`$ ${totalUsers * currentRate} x ${setPlanIntervalInMonths()}`}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={6}>
                {mode != 'SUBSCRIPTION' && <CouponLisiting onCouponSubmit={onCouponSubmit} />}
              </Grid>
              <Grid item xs={2}></Grid>
              <Grid item xs={4} mt={1}>
                <AlingTextSideBySide leftText="Subtotal:" rightText={`$${totalCharge}`} />
                <AlingTextSideBySide leftText="Sales Tax:" rightText={`$0`} />
                {mode === 'SUBSCRIPTION' ? (
                  <>
                    <AlingTextSideBySide
                      leftText="Coupon:"
                      rightText={
                        subInfo?.coupon && subInfo?.coupon != 'null' ? subInfo.coupon : '---'
                      }
                      primary={subInfo?.coupon ? true : false}
                    />
                    <AlingTextSideBySide
                      leftText="Total Discount:"
                      rightText={
                        subInfo?.couponDiscount && subInfo?.couponDiscount != 'null'
                          ? `${subInfo.couponDiscount}%`
                          : '0%'
                      }
                    />
                  </>
                ) : (
                  <>
                    {coupon?.code ? (
                      <>
                        <Alert
                          sx={{ cursor: 'pointer', mt: 1 }}
                          onClick={handleCoupon}
                          severity={coupon?.code ? 'success' : 'info'}
                          action={
                            <HighlightOffIcon
                              sx={{ color: 'red', cursor: 'pointer' }}
                              onClick={removeCoupon}
                            />
                          }
                        >
                          <WsTypography bold>{`'${coupon.code}' applied`}</WsTypography>
                          <WsTypography bold variant={WsTypoVariants.BODY2}>
                            {`Enjoy your ${coupon?.coupon.percent_off}% off`}
                          </WsTypography>
                        </Alert>
                        <Divider sx={{ width: '100%', mt: 1 }} />
                      </>
                    ) : (
                      <Grid container mt={1}>
                        <Grid item xs={6} textAlign="left">
                          <Box mt={0.5}>
                            <WsTypography bold> Coupon</WsTypography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <WsButton variant="text" size="small" onClick={handleCoupon}>
                            Apply Coupon
                          </WsButton>
                        </Grid>
                        <Divider sx={{ width: '100%', mt: 1 }} />
                      </Grid>
                    )}
                    <AlingTextSideBySide
                      leftText="Total Discount:"
                      rightText={`${
                        coupon?.coupon?.percent_off ? coupon?.coupon?.percent_off : 0
                      }%`}
                    />
                  </>
                )}
                <Grid container mt={2}>
                  <Grid item xs={8} textAlign="left">
                    <Stack direction="row" spacing={0}>
                      <WsTypography bold>Prorated Amount:</WsTypography>
                      <Tooltip title="The amount which will be charged from today to the end of your billing cycle">
                        <InfoIcon />
                      </Tooltip>
                    </Stack>
                  </Grid>
                  <Grid item xs={4} textAlign="right">
                    <WsTypography bold>
                      {proratedAmount != '0' && proratedAmount != 'calculating'
                        ? `$${proratedAmount}`
                        : proratedAmount}
                    </WsTypography>
                  </Grid>
                  <Divider sx={{ width: '100%', mt: 1 }} />
                </Grid>
                <Grid container mt={2}>
                  <Grid item xs={8} textAlign="left">
                    <Stack direction="row" spacing={0}>
                      <WsTypography bold>Net Payable:</WsTypography>
                      <Tooltip title="The amount which will be charged from today to the end of your billing cycle">
                        <InfoIcon />
                      </Tooltip>
                    </Stack>
                  </Grid>
                  <Grid item xs={4} textAlign="right">
                    <WsTypography bold>
                      {proratedAmount != '0' && proratedAmount != 'calculating'
                        ? `$${proratedAmount}`
                        : proratedAmount}
                    </WsTypography>
                  </Grid>
                  <Divider sx={{ width: '100%', mt: 1 }} />
                </Grid>
                <Stack mt={2}>
                  <WsButton
                    size="large"
                    onClick={() => setWarnBeforePayment(true)}
                    disabled={
                      disableSubmit || proratedAmount === '0' || proratedAmount === 'calculating'
                    }
                  >
                    Pay now
                  </WsButton>
                </Stack>
              </Grid>
            </FormProvider>
          ) : (
            <>
              <Grid item xs={12} textAlign="center">
                <Typography
                  color={WsTypoColors.PRIMARY}
                  variant="h5"
                  sx={{ fontSize: '25px', fontWeight: 'bold' }}
                >
                  Purchase User
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Item>
                  <Typography variant="h6">
                    Please note that an amount of
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'bold', display: 'inline', ml: 1, mr: 1 }}
                      color={WsTypoColors.BLACK}
                    >
                      {`₹ ${proratedAmount}`}
                    </Typography>
                    will be charged to your account when you make this payment.
                  </Typography>
                  <Stack direction="row" spacing={2} mt={3} justifyContent="center">
                    <WsButton onClick={handleClose} variant="outlined">
                      Cancel
                    </WsButton>
                    <Button
                      sx={{ width: '12%' }}
                      variant="contained"
                      disabled={disableSubmit}
                      onClick={onSubmit}
                    >
                      Pay
                    </Button>
                  </Stack>
                </Item>
              </Grid>
            </>
          )}
        </Grid>
      )}
    </>
  );
};

export default ManagePlan;
